import { type CSSProperties, type Ref, type FC, type ReactNode, type ReactElement } from 'react'
import classnames from 'classnames'
import { type IActionsProps, type IIconButtonProps } from 'shared/ui'
import { IconButton } from 'shared/ui/IconButton'
import { Typography, type ITypographyProps } from 'shared/ui/Typography'
import { type ITooltipProps } from 'shared/ui/Tooltip'
import { Icon, type IIconProps } from 'shared/ui/Icon'
import { type ActionItem, Actions } from 'shared/ui/Actions/Actions'
import { Divider } from 'shared/ui/Divider/Divider'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

export type IContainerHeaderProps = {
  innerRef?: Ref<HTMLDivElement>
  loading?: boolean
  title?: ReactNode
  titleVariant?: ITypographyProps['variant']
  customTitle?: ReactNode | null
  titleIconProps?: IIconProps
  titleIconButtonProps?: Partial<IIconButtonProps>
  titleIconAction?: () => void
  isEmoji?: boolean
  actions?: ActionItem[]
  actionsProps?: IActionsProps | null
  isBorderBottom?: boolean
  isBorderLeft?: boolean
  isBorderRight?: boolean
  isAutoHeight?: boolean
  variant?: 'primary' | 'transparent'
  ellipsis?: boolean
  type?: 'title' | 'container'
  container?: ReactElement
  paddingRight?: CSSProperties['paddingRight']
  paddingLeft?: CSSProperties['paddingLeft']
  height?: number
  paddingTop?: CSSProperties['paddingTop']
  paddingBottom?: CSSProperties['paddingBottom']
  titleCount?: number
  showCount?: boolean
  maxContentWidth?: number
  rightTitleContent?: ReactNode
  actionIconTooltipProps?: ITooltipProps
  actionsGap?: number
  ariaLabel?: string
  className?: string
  style?: CSSProperties
  isTitleDivider?: boolean
}

const ContainerHeader: FC<IContainerHeaderProps> = (props) => {
  const {
    title = '',
    titleVariant,
    customTitle = null,
    titleIconProps,
    titleIconButtonProps,
    titleIconAction,
    isBorderBottom = false,
    isBorderRight = false,
    isBorderLeft = false,
    isTitleDivider = false,
    variant = 'primary',
    innerRef,
    ellipsis,
    loading,
    actions,
    actionIconTooltipProps,
    type = 'title',
    container = '',
    paddingRight,
    paddingLeft,
    paddingTop,
    paddingBottom,
    titleCount = 0,
    showCount = false,
    maxContentWidth,
    rightTitleContent,
    actionsGap,
    actionsProps,
    height,
    ariaLabel,
    className,
    isAutoHeight,
    style,
  } = props

  const styleWrap = {
    ...(paddingRight !== undefined
      ? {
          paddingRight,
        }
      : null),
    ...(paddingLeft !== undefined
      ? {
          paddingLeft,
        }
      : null),
    ...(paddingTop !== undefined
      ? {
          paddingTop,
        }
      : null),
    ...(paddingBottom !== undefined
      ? {
          paddingBottom,
        }
      : null),
    height: height ? `${height}px` : 'auto',
    ...style,
  }

  if (type === 'container') {
    return (
      <div
        ref={innerRef}
        className={classnames(
          styles.wrap,
          styles[variant],
          {
            [styles.isBorderBottom]: isBorderBottom,
            [styles.isBorderRight]: isBorderRight,
            [styles.isBorderLeft]: isBorderLeft,
          },
          className
        )}
        style={{
          ...styleWrap,
        }}
      >
        {container}
      </div>
    )
  }

  return (
    <div
      ref={innerRef}
      className={classnames(
        styles.wrap,
        styles[variant],
        {
          [styles.isBorderBottom]: isBorderBottom,
          [styles.isBorderRight]: isBorderRight,
          [styles.isBorderLeft]: isBorderLeft,
          [styles.isAutoHeight]: isAutoHeight,
        },
        className
      )}
      style={styleWrap}
    >
      <div className={styles.content} style={{ maxWidth: maxContentWidth }}>
        <div className={styles.box}>
          <div
            className={classnames(
              styles.title,
              titleIconProps && styles.titleIcon,
              rightTitleContent && styles.rightTitleContent,
              rightTitleContent && titleIconProps && styles.fullContent
            )}
            data-name='container_header_title'
            aria-label={
              ariaLabel
                ? getAriaLabel(`${ariaLabel} containerHeader`)
                : getAriaLabel('ContainerHeaderSelector')
            }
          >
            {loading ? (
              <div className={styles.skeletonTitle}></div>
            ) : (
              <>
                {titleIconProps && !Boolean(titleIconAction) && (
                  <div
                    className={classnames(styles.titleIcon)}
                    data-name='container_header_title_icon'
                  >
                    <Icon {...titleIconProps} />
                  </div>
                )}
                {titleIconProps && Boolean(titleIconAction) && (
                  <div
                    className={classnames(styles.titleActionIcon, {
                      [styles.titleActionIconWrap]: isTitleDivider,
                    })}
                    data-name='container_header_title_icon'
                  >
                    <IconButton
                      iconProps={{
                        fontSize: titleIconProps.fontSize,
                        icon: titleIconProps.icon,
                      }}
                      tooltipProps={actionIconTooltipProps}
                      variant={'icon'}
                      size={titleIconButtonProps?.size ?? 'medium'}
                      color={'secondary'}
                      onClick={() => {
                        titleIconAction?.()
                      }}
                      ariaLabel='ContainerHeader_titleIcon'
                    />
                    {isTitleDivider && (
                      <Divider
                        orientation='vertical'
                        sx={{ height: '16px !important', marginLeft: '8px' }}
                      />
                    )}
                  </div>
                )}

                <Typography
                  variant={titleVariant || 'body-lg-medium'}
                  ellipsis={ellipsis}
                  className={classnames({
                    [styles.customTitle]: customTitle,
                  })}
                  ariaLabel={'PageTitle'}
                >
                  {customTitle || title}
                  {showCount && <span className={styles.count}> {titleCount}</span>}
                </Typography>

                {rightTitleContent && (
                  <div className={styles.rightTitleContentWrap}> {rightTitleContent}</div>
                )}
              </>
            )}
          </div>
        </div>

        {(actions || actionsProps) && (
          <div className={styles.buttons}>
            {loading ? (
              <div className={styles.skeletonRight} />
            ) : (
              <>
                {(actions || actionsProps) && (
                  <Actions actions={actions || []} gap={actionsGap} {...actionsProps} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export { ContainerHeader }
